import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Target, Loader2, Play, Clock, CheckCircle2, Circle, Wrench, FileText, AlertTriangle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLE = {
  Completed: "bg-emerald-50 text-emerald-700",
  Running: "bg-blue-50 text-blue-700",
  Queued: "bg-slate-100 text-slate-600",
  "Waiting for Approval": "bg-amber-50 text-amber-700",
  Blocked: "bg-orange-50 text-orange-700",
  Failed: "bg-red-50 text-red-600",
};

function StatusChip({ status }) {
  return <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

export default function MissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => { api.get(`/missions/${id}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <div className="animate-fade-up py-16 text-center text-[13px] text-slate-400" data-testid="mission-not-found">Mission not found. <Link to="/owner/missions" className="text-[#1a5fd0]">Back to Missions</Link></div>;
  if (!data) return <Loading />;
  const { mission, timeline, tools, runs } = data;

  const run = async () => {
    setRunning(true);
    try { const { data } = await api.post(`/missions/${mission.id}/run`); setData(data); toast.success("Mission executed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run mission."); } finally { setRunning(false); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="mission-detail-page">
      <button onClick={() => navigate("/owner/missions")} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft size={15} /> Missions</button>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1a5fd0]/10 text-[#1a5fd0]"><Target size={19} /></span>
            <div>
              <div className="font-head text-[17px] font-bold text-slate-900" data-testid="mission-title">{mission.title}</div>
              <div className="mt-1 font-mono text-[10px] text-slate-400">Opened {new Date(mission.when).toLocaleString()}</div>
            </div>
          </div>
          <StatusChip status={mission.status} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {mission.assignees.map((a) => <span key={a} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">{a}</span>)}
        </div>
        {mission.status !== "Completed" && (
          <button onClick={run} disabled={running} data-testid="run-mission-btn" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} {running ? "Running…" : "Run this mission"}
          </button>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="p-5" data-testid="mission-deliverables">
            <div className="mb-3 flex items-center gap-2"><FileText size={15} className="text-[#b0862f]" /><span className="font-head text-[14px] font-bold text-slate-900">Results & deliverables</span></div>
            {(!mission.deliverables || mission.deliverables.length === 0) ? (
              <div className="py-6 text-center text-[13px] text-slate-400">No deliverables yet. Run the mission to have your Capital Crew produce the work.</div>
            ) : (
              <div className="space-y-4">
                {mission.deliverables.map((d, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 p-4" data-testid="deliverable-item">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="font-head text-[13px] font-bold text-slate-900">{d.agent} <span className="font-normal text-slate-400">· {d.role}</span></span>
                      {d.handoff && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">{d.handoff}</span>}
                    </div>
                    <div className="prose prose-sm max-w-none text-[13px] text-slate-600"><ReactMarkdown>{d.result}</ReactMarkdown></div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-5" data-testid="mission-timeline">
            <div className="mb-3 flex items-center gap-2"><Clock size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Activity timeline</span></div>
            <div className="space-y-3">
              {timeline.map((e, i) => (
                <div key={i} className="flex gap-2.5">
                  <div className="mt-0.5">{e.kind === "done" ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Circle size={14} className="text-[#1a5fd0]" />}</div>
                  <div className="min-w-0">
                    <div className="text-[12px] text-slate-700">{e.label}</div>
                    <div className="font-mono text-[9px] text-slate-400">{e.actor}{e.model ? ` · ${e.model}` : ""} · {new Date(e.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5" data-testid="mission-steps">
            <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Steps</div>
            <div className="space-y-2">
              {mission.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px]">
                  {s.done ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Circle size={14} className="text-slate-300" />}
                  <span className={s.done ? "text-slate-700" : "text-slate-400"}>{s.label}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5" data-testid="mission-tools">
            <div className="mb-3 flex items-center gap-2"><Wrench size={15} className="text-slate-400" /><span className="font-head text-[14px] font-bold text-slate-900">Tools & models used</span></div>
            {tools.length === 0 ? (
              <div className="flex items-center gap-1.5 text-[12px] text-slate-400"><AlertTriangle size={13} /> No model calls recorded for this mission yet.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">{tools.map((t) => <span key={t} className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{t}</span>)}</div>
            )}
            <div className="mt-2 font-mono text-[10px] text-slate-400">{runs.length} model run{runs.length === 1 ? "" : "s"} recorded</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
